import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, SimpleChanges, signal } from "@angular/core";
import { Chart, Filler, Plugin, TooltipModel } from "chart.js";

import { COLOR_BSC, GraficaBsc } from "src/app/bsc/graficas/grafica-bsc";
import { fechaHora, numero } from "../cc-formato";
import { MS_GRAFICA, menosMovimiento } from "./movimiento-cc";
import {
  FormatoSerieCc,
  LineaGlobo,
  PuntoSerieCc,
  SentidoMetaCc,
  SerieCc,
  TextoGlobo,
  marcasDeFechas,
  sinCeroDecimal,
  techoSerie,
  textoGlobo,
  textoMeta,
  valorSerie,
} from "./serie-cc-lectura";

Chart.register(Filler);

export type { FormatoSerieCc, PuntoSerieCc, SentidoMetaCc } from "./serie-cc-lectura";

/** El globo a la vista: su texto y dónde va respecto al lienzo (a la derecha, a la izquierda o abajo del punto). */
export interface GloboVista extends TextoGlobo {
  x: number;
  y: number;
  lado: "der" | "izq" | "abajo";
}

/** Lo que pinta el plugin de la meta: su rótulo junto a la línea, con fondo para que se lea sobre la serie. */
interface RotuloMeta {
  texto: string;
  valor: number;
  color: string;
  fondo: string;
  /** Arriba o abajo de la línea, donde estorbe menos a la serie. */
  arriba: boolean;
}

/** Ancho del globo y su separación del punto, en píxeles (el CSS usa el mismo ancho: 272px). */
const ANCHO_GLOBO = 272;
const SEPARACION_GLOBO = 14;
const MS_HOVER = 160;
const FUENTE_EJE = 12;
/** Lo que ocupa a lo ancho el eje de los valores, para repartir las fechas en el resto. */
const ANCHO_EJE_Y = 56;

/** El rótulo de la meta, pintado junto a su línea con su color, sobre una pastilla del color de la tarjeta. */
const ROTULO_META: Plugin<"line"> = {
  id: "ccRotuloMeta",
  afterDatasetsDraw(chart) {
    const rotulo = (chart.options.plugins as { ccRotulo?: RotuloMeta }).ccRotulo;
    if (!rotulo) {
      return;
    }
    const area = chart.chartArea;
    const y = chart.scales["y"].getPixelForValue(rotulo.valor);
    const ctx = chart.ctx;
    ctx.save();
    ctx.font = `600 ${FUENTE_EJE}px Roboto, 'Segoe UI', sans-serif`;
    const ancho = ctx.measureText(rotulo.texto).width + 10;
    const alto = 16;
    const cabeArriba = y - alto - 2 >= area.top - 4;
    const cabeAbajo = y + alto + 2 <= area.bottom;
    const arriba = (rotulo.arriba && cabeArriba) || !cabeAbajo;
    const top = arriba ? y - alto - 2 : y + 2;
    const left = area.left + 6;
    ctx.fillStyle = rotulo.fondo;
    ctx.beginPath();
    ctx.roundRect(left, top, ancho, alto, 6);
    ctx.fill();
    ctx.fillStyle = rotulo.color;
    ctx.textBaseline = "middle";
    ctx.fillText(rotulo.texto, left + 5, top + alto / 2 + 0.5);
    ctx.restore();
  },
};

/** La guía vertical del punto activo: dice a qué análisis corresponde el globo. */
const GUIA: Plugin<"line"> = {
  id: "ccGuia",
  beforeDatasetsDraw(chart) {
    const activo = chart.getActiveElements()[0];
    const color = (chart.options.plugins as { ccGuia?: { color: string } }).ccGuia?.color;
    if (!activo || !color) {
      return;
    }
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(activo.element.x, chart.chartArea.top);
    ctx.lineTo(activo.element.x, chart.chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

/**
 * Calidad de código · una serie del historial (cobertura, duplicación o
 * hallazgos abiertos) de los últimos análisis guardados, con su color, su meta
 * como línea punteada rotulada («Meta: 80 % o más») y un eje de fechas que no
 * repite el día. Al pasar el cursor (o con ← → desde el teclado) un globo dice
 * de cada análisis: fecha y hora, el valor, el cambio contra el anterior, si
 * cumple la meta, si pasó el Quality Gate, versión y commit. Chart.js directo
 * sobre la base del BSC (grafica-bsc.ts: colores del tema y repintado al
 * cambiar de tema). Entrada de 700 ms (easeOutQuart) y globo de 180 ms;
 * ninguna animación con prefers-reduced-motion. Las fechas, en hora de México.
 */
@Component({
  selector: "app-serie-cc",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="cc-serie">
      <canvas #lienzo role="img" tabindex="0" [attr.aria-label]="textoAccesible" (keydown)="teclado($event)" (blur)="cierra()"></canvas>
      <p class="cc-serie__vacio" *ngIf="!hayDatos">Sin análisis guardados</p>
      <div class="cc-globo" aria-hidden="true" *ngIf="globo() as g" [class.is-abierto]="abierto()" [ngClass]="'is-' + g.lado"
           [style.left.px]="g.x" [style.top.px]="g.y">
        <strong class="cc-globo__titulo">{{ g.titulo }}</strong>
        <span class="cc-globo__valor cc-num"><span class="cc-globo__punto"></span>{{ g.valor }}</span>
        <span class="cc-globo__linea" *ngFor="let l of lineas(g)" [ngClass]="l.clase">
          <span class="material-icons">{{ l.icono }}</span>{{ l.texto }}
        </span>
        <span class="cc-globo__datos" *ngIf="g.datos">{{ g.datos }}</span>
      </div>
    </div>
  `,
  styles: `
    :host {
      --cc-globo-fondo: #ffffff;
      --cc-globo-sombra: 0 12px 28px -8px rgba(15, 23, 42, 0.3), 0 2px 6px rgba(15, 23, 42, 0.08);
      display: block;
    }

    :host-context(html[data-tema="oscuro"]) {
      --cc-globo-fondo: #1c2129;
      --cc-globo-sombra: 0 14px 32px -8px rgba(0, 0, 0, 0.7), 0 2px 6px rgba(0, 0, 0, 0.4);
    }

    :host-context(html[data-tema="cristal"]) {
      --cc-globo-fondo: var(--cos-globo-fondo);
      --cc-globo-sombra: 0 12px 28px -8px rgba(var(--cos-sombra-rgb), 0.3), 0 2px 6px rgba(var(--cos-sombra-rgb), 0.08);
    }

    /* El alto lo puede bajar la pantalla en ventanas bajas (--cc-serie-alto). */
    .cc-serie {
      height: var(--cc-serie-alto, 100px);
      position: relative;
      width: 100%;
    }

    canvas {
      border-radius: 8px;
      cursor: crosshair;
    }

    canvas:focus-visible {
      outline: 2px solid var(--cos-acento, #b71c1c);
      outline-offset: 2px;
    }

    /* Sin serie, el hueco del lienzo lo dice en vez de quedar en blanco. */
    .cc-serie__vacio {
      color: var(--cos-texto-2, #475569);
      font-size: 0.84rem;
      font-weight: 400;
      inset: 0;
      margin: 0;
      padding-top: 40px;
      position: absolute;
      text-align: center;
    }

    /* El globo: opaco en los tres temas, con el texto del tema; entra con opacidad y un desliz corto. */
    .cc-globo {
      background: var(--cc-globo-fondo);
      border: 1px solid var(--cos-borde, #d8e0ea);
      border-radius: 12px;
      box-shadow: var(--cc-globo-sombra);
      box-sizing: border-box;
      color: var(--cos-texto, #253042);
      display: flex;
      flex-direction: column;
      font-size: 0.8rem;
      font-weight: 400;
      gap: 3px;
      line-height: 1.35;
      max-width: calc(100vw - 32px);
      opacity: 0;
      padding: 9px 12px 10px;
      pointer-events: none;
      position: absolute;
      transform: translateY(4px) scale(0.98);
      transition:
        opacity 180ms cubic-bezier(0.23, 1, 0.32, 1),
        transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
      width: 272px;
      z-index: 20;
    }

    .cc-globo.is-abierto {
      opacity: 1;
      transform: none;
    }

    .cc-globo__titulo {
      font-size: 0.8rem;
      font-weight: 700;
    }

    .cc-globo__valor {
      align-items: center;
      display: flex;
      font-size: 0.92rem;
      font-weight: 600;
      gap: 6px;
    }

    .cc-globo__punto {
      background: var(--cc-serie-color, var(--cos-info, #1d4ed8));
      border-radius: 50%;
      flex: 0 0 auto;
      height: 10px;
      width: 10px;
    }

    .cc-globo__linea {
      align-items: flex-start;
      color: var(--cos-texto-2, #475569);
      display: flex;
      font-weight: 500;
      gap: 5px;
    }

    .cc-globo__linea .material-icons {
      flex: 0 0 auto;
      font-size: 16px;
      line-height: 1.1;
    }

    .cc-globo__linea.is-pasa,
    .cc-globo__linea.is-mejor {
      color: var(--cos-exito-texto, #166534);
    }

    .cc-globo__linea.is-no-pasa,
    .cc-globo__linea.is-peor {
      color: var(--cos-peligro, #b91c1c);
    }

    .cc-globo__datos {
      border-top: 1px solid var(--cos-borde, #d8e0ea);
      color: var(--cos-texto-2, #475569);
      font-weight: 400;
      margin-top: 3px;
      overflow-wrap: anywhere;
      padding-top: 5px;
    }

    @media (prefers-reduced-motion: reduce) {
      .cc-globo {
        transform: none;
        transition: none;
      }
    }
  `,
  host: { "[style.--cc-serie-color]": "tono" },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SerieCcComponent extends GraficaBsc {
  @Input() nombre = "";
  /** ISO UTC, del más viejo al más nuevo. */
  @Input() fechas: string[] | null | undefined = [];
  @Input() valores: (number | null)[] | null | undefined = [];
  @Input() formato: FormatoSerieCc = "PCT";
  /** El color de la serie: un token CSS («var(--cc-serie-cobertura, #0f766e)») que se resuelve con el tema vigente. */
  @Input() tono: string = COLOR_BSC.real;
  /** La meta del Quality Gate (línea punteada); nula = sin línea. */
  @Input() meta: number | null | undefined = null;
  @Input() sentido: SentidoMetaCc = "MAS";
  /** true si bajar es mejorar (duplicación, hallazgos): pinta el cambio en verde o en rojo. */
  @Input() menosEsMejor = false;
  /** "hallazgos": lo que cuenta una serie de enteros, para el globo. */
  @Input() unidad = "";
  /** Gate, versión y commit de cada análisis, en el mismo orden que las fechas. */
  @Input() puntos: (PuntoSerieCc | null | undefined)[] | null | undefined = [];

  protected readonly globo = signal<GloboVista | null>(null);
  protected readonly abierto = signal(false);
  private lineal: Chart<"line"> | null = null;
  private marcas = new Map<number, string>();
  private indice = -1;

  /** Si solo cambian gate, versión y commit (llegan después), la gráfica no se vuelve a dibujar: solo los usa el globo. */
  override ngOnChanges(cambios?: SimpleChanges): void {
    if (cambios && Object.keys(cambios).every((k) => k === "puntos")) {
      return;
    }
    super.ngOnChanges();
  }

  /** Hay al menos una fecha que pintar. */
  get hayDatos(): boolean {
    return (this.fechas ?? []).length > 0;
  }

  get textoAccesible(): string {
    const fechas = this.fechas ?? [];
    const puntos = (this.valores ?? []).map((v, i) => `${fechaHora(fechas[i])}: ${valorSerie(v, this.formato)}`).join("; ");
    const meta = this.metaVigente == null ? "" : ` (${textoMeta(this.metaVigente, this.formato, this.sentido)})`;
    const guia = puntos ? " Con las flechas izquierda y derecha se recorre cada análisis." : "";
    return `${this.nombre}${meta}. ${puntos || "Sin análisis guardados"}.${guia}`;
  }

  /** La meta si es un número; null si no hay. */
  private get metaVigente(): number | null {
    return numero(this.meta);
  }

  /** La serie con su contexto, para los textos del globo. */
  private get serie(): SerieCc {
    return {
      nombre: this.nombre,
      fechas: this.fechas ?? [],
      valores: (this.valores ?? []).map((v) => numero(v)),
      formato: this.formato,
      meta: this.metaVigente,
      sentido: this.sentido,
      menosEsMejor: this.menosEsMejor,
      unidad: this.unidad,
      puntos: this.puntos ?? [],
    };
  }

  /** Las líneas del globo que traen algo que decir, en orden: cambio, meta y gate. */
  protected lineas(g: GloboVista): LineaGlobo[] {
    return [g.cambio, g.meta, g.gate].filter((l): l is LineaGlobo => l != null);
  }

  /** ← → recorren los análisis, Inicio y Fin van a los extremos, Esc cierra el globo. */
  teclado(evento: KeyboardEvent): void {
    const n = (this.fechas ?? []).length;
    const grafica = this.lineal;
    if (!grafica || n === 0) {
      return;
    }
    const siguiente = this.indiceTras(evento.key, n);
    if (siguiente === null) {
      return;
    }
    evento.preventDefault();
    if (siguiente < 0) {
      this.cierra();
      return;
    }
    this.indice = siguiente;
    const punto = grafica.getDatasetMeta(0).data[siguiente];
    const activos = [{ datasetIndex: 0, index: siguiente }];
    grafica.setActiveElements(activos);
    grafica.tooltip?.setActiveElements(activos, { x: punto?.x ?? 0, y: punto?.y ?? 0 });
    grafica.update();
  }

  /** El índice al que lleva una tecla; -1 para cerrar; null si la tecla no es de la gráfica. */
  private indiceTras(tecla: string, n: number): number | null {
    const actual = this.indice;
    switch (tecla) {
      case "ArrowRight":
        return actual < 0 ? n - 1 : Math.min(n - 1, actual + 1);
      case "ArrowLeft":
        return actual < 0 ? n - 1 : Math.max(0, actual - 1);
      case "Home":
        return 0;
      case "End":
        return n - 1;
      case "Escape":
        return -1;
      default:
        return null;
    }
  }

  /** Cierra el globo y apaga el punto activo. */
  cierra(): void {
    this.indice = -1;
    this.abierto.set(false);
    const grafica = this.lineal;
    if (grafica && grafica.getActiveElements().length > 0) {
      grafica.setActiveElements([]);
      grafica.tooltip?.setActiveElements([], { x: 0, y: 0 });
      grafica.update();
    }
  }

  /** Chart.js avisa de su tooltip (que no pinta): aquí se arma el globo y se coloca junto al punto. */
  alGlobo(tooltip: Pick<TooltipModel<"line">, "opacity" | "dataPoints" | "caretX" | "caretY">, lienzo: HTMLCanvasElement): void {
    const punto = tooltip.dataPoints?.find((d) => d.datasetIndex === 0);
    if (tooltip.opacity === 0 || !punto) {
      this.abierto.set(false);
      return;
    }
    this.indice = punto.dataIndex;
    const posicion = colocaGlobo(lienzo, tooltip.caretX, tooltip.caretY);
    this.globo.set({ ...textoGlobo(this.serie, punto.dataIndex), ...posicion });
    this.abierto.set(true);
  }

  protected arma(lienzo: HTMLCanvasElement): Chart | null {
    this.lineal = null;
    this.indice = -1;
    this.abierto.set(false);
    const s = this.serie;
    if (s.fechas.length === 0) {
      return null;
    }
    const serie = this.color(this.tono || COLOR_BSC.real);
    const tenue = this.color(COLOR_BSC.programado);
    const fondo = this.color("var(--cc-superficie, var(--cos-superficie-elevada, #ffffff))");
    const maximo = techoSerie(s.valores, s.meta, s.formato);
    this.marcas = marcasDeFechas(s.fechas, Math.max(120, (lienzo.parentElement?.clientWidth ?? 300) - ANCHO_EJE_Y));
    const datos: { label: string; data: (number | null)[]; [k: string]: unknown }[] = [
      {
        label: this.nombre,
        data: s.valores,
        borderColor: serie,
        backgroundColor: conAlfa(serie, 0.12),
        fill: "origin",
        borderWidth: 2,
        tension: 0.3,
        pointRadius: s.valores.length > 12 ? 2 : 3,
        pointHoverRadius: 6,
        pointBackgroundColor: serie,
        pointHoverBorderColor: fondo,
        pointHoverBorderWidth: 2,
        spanGaps: true,
      },
    ];
    if (s.meta != null) {
      datos.push({
        label: textoMeta(s.meta, s.formato, s.sentido),
        data: s.valores.map(() => s.meta),
        borderColor: tenue,
        borderDash: [5, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      });
    }
    // Las opciones de los plugins propios (rótulo de la meta y guía), que Chart.js no conoce por su tipo.
    const propios: Record<string, unknown> = { ccGuia: { color: conAlfa(tenue, 0.55) } };
    if (s.meta != null) {
      propios["ccRotulo"] = rotuloMeta(s, tenue, fondo);
    }
    const sinMovimiento = menosMovimiento();
    const grafica = new Chart(lienzo, {
      type: "line",
      data: { labels: s.fechas.map((_, i) => String(i)), datasets: datos },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: sinMovimiento ? false : { duration: MS_GRAFICA, easing: "easeOutQuart" },
        transitions: { active: { animation: { duration: sinMovimiento ? 0 : MS_HOVER } } },
        interaction: { mode: "index", intersect: false },
        onResize: (_, tamano) => {
          this.marcas = marcasDeFechas(s.fechas, Math.max(120, tamano.width - ANCHO_EJE_Y));
        },
        layout: { padding: { right: 6 } },
        scales: {
          x: {
            ticks: { color: tenue, font: { size: FUENTE_EJE }, maxRotation: 0, autoSkip: false, callback: (_, i) => this.marcas.get(i) ?? "" },
            grid: { display: false },
            border: { color: this.color(COLOR_BSC.rejilla) },
          },
          y: {
            min: 0,
            max: maximo,
            ticks: { color: tenue, font: { size: FUENTE_EJE }, maxTicksLimit: 3, callback: (v) => sinCeroDecimal(valorSerie(Number(v), s.formato)) },
            grid: { color: this.color(COLOR_BSC.rejilla) },
            border: { display: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false, external: ({ tooltip }) => this.alGlobo(tooltip, lienzo) },
          ...propios,
        },
      },
      plugins: [ROTULO_META, GUIA],
    });
    this.lineal = grafica;
    return grafica;
  }
}

/**
 * Dónde va el globo, en coordenadas del lienzo: a la derecha del punto si
 * cabe, si no a la izquierda; si no cabe de ningún lado, abajo del punto.
 * «Cabe» es dentro del contenedor marcado con data-cc-globos (la pestaña, que
 * recorta lo que se sale) o, sin él, de la ventana.
 */
export function colocaGlobo(lienzo: HTMLElement, x: number, y: number): { x: number; y: number; lado: GloboVista["lado"] } {
  const caja = lienzo.getBoundingClientRect();
  const limite = lienzo.closest("[data-cc-globos]")?.getBoundingClientRect() ?? { left: 0, right: globalThis.innerWidth || caja.right };
  const min = limite.left - caja.left + 4;
  const max = limite.right - caja.left - 4;
  const ancho = Math.min(ANCHO_GLOBO, max - min);
  const arriba = Math.max(-8, y - 28);
  if (x + SEPARACION_GLOBO + ancho <= max) {
    return { x: x + SEPARACION_GLOBO, y: arriba, lado: "der" };
  }
  if (x - SEPARACION_GLOBO - ancho >= min) {
    return { x: x - SEPARACION_GLOBO - ancho, y: arriba, lado: "izq" };
  }
  return { x: Math.min(Math.max(min, x - ancho / 2), max - ancho), y: y + SEPARACION_GLOBO, lado: "abajo" };
}

/** El rótulo de la meta: arriba de la línea si la serie, a la izquierda, queda abajo de ella; si no, abajo. */
function rotuloMeta(s: SerieCc, color: string, fondo: string): RotuloMeta {
  const meta = s.meta ?? 0;
  const izquierda = s.valores.slice(0, Math.max(1, Math.ceil(s.valores.length / 3))).filter((v): v is number => v != null);
  const promedio = izquierda.length > 0 ? izquierda.reduce((a, b) => a + b, 0) / izquierda.length : meta;
  return { texto: textoMeta(meta, s.formato, s.sentido), valor: meta, color, fondo: conAlfa(fondo, 0.92), arriba: promedio <= meta };
}

/** "rgb(r, g, b)" o "rgba(r, g, b, a)" → la misma con la transparencia pedida. */
function conAlfa(color: string, alfa: number): string {
  const m = /rgba?\(([^)]+)\)/.exec(color);
  if (!m) {
    return color;
  }
  const [r, g, b] = m[1].split(",").map((x) => x.trim());
  return `rgba(${r}, ${g}, ${b}, ${alfa})`;
}
